import { Component, Inject, OnInit, ViewChild, TemplateRef } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormArray, Validators } from '@angular/forms';
import _ from 'lodash';
import { switchMap } from 'rxjs/operators';
import { GeneralDataService } from '../../../../services/general-data-service';
import { FilterGroup, FilterCondition, FilterGroupsService } from '@services/filter-groups.service';
import { FilterGroupValuesDialogComponent } from '../filter-group-values-dialog/filter-group-values-dialog.component';
import { FilterGroupLogsDialogComponent } from '../filter-group-logs-dialog/filter-group-logs-dialog.component';
import { ConfigService } from '@services/config.service';

export interface FilterGroupDialogData {
  filterGroup?: FilterGroup;
}

@Component({
  selector: 'app-filter-group-dialog',
  templateUrl: './filter-group-dialog.component.html',
  standalone: false
})
export class FilterGroupDialogComponent implements OnInit {

  @ViewChild('confirmRerunTmpl') confirmRerunTmpl: TemplateRef<any>;

  form: UntypedFormGroup;
  availableFields: any[] = [];
  derivedFields: any[] = [];
  fieldValues: { [fieldKey: string]: string[] } = {};
  private fieldRawValues: { [fieldKey: string]: any[] } = {};
  private filterGroupMap: { [derivedFieldName: string]: FilterGroup } = {};
  fieldSortPreference: { [fieldKey: string]: 'alpha' | 'numeric' } = {};
  isSaving = false;
  isRerunning = false;
  errorMessage = '';

  constructor(
    public dialogRef: MatDialogRef<FilterGroupDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: FilterGroupDialogData,
    private fb: UntypedFormBuilder,
    private generalDataService: GeneralDataService,
    private filterGroupsService: FilterGroupsService,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.buildForm();
    this.loadFields();
    this.loadFilterGroupMap();
  }

  get conditions(): UntypedFormArray {
    return this.form.get('conditions') as UntypedFormArray;
  }

  get derivedFieldSuffix(): string {
    const val: string = this.form.get('derivedFieldName').value || '';
    return val.startsWith('cmpz~') ? val.slice(5) : val;
  }

  onDerivedSuffixInput(suffix: string): void {
    this.form.get('derivedFieldName').setValue(suffix ? `cmpz~${suffix}` : '');
    if (this.conditions.length > 0) {
      this.conditions.controls[0].get('fieldKey').enable();
    }
  }

  onDerivedFieldSelected(suffix: string): void {
    const fullKey = `cmpz~${suffix}`;
    this.form.get('derivedFieldName').setValue(fullKey);

    if (this.conditions.length === 0) { return; }
    const previousGroup = this.filterGroupMap[fullKey];
    const previousCondition = previousGroup?.conditions?.[0];
    if (!previousCondition) { return; }

    const firstCtrl = this.conditions.controls[0];
    firstCtrl.get('fieldKey').setValue(previousCondition.fieldKey);
    firstCtrl.get('fieldLabel').setValue(previousCondition.fieldLabel);
    firstCtrl.get('fieldHash').setValue(previousCondition.fieldHash);
    firstCtrl.get('values').setValue([]);
    firstCtrl.get('fieldKey').disable();
    this.loadFieldValues(previousCondition.fieldKey, previousCondition.fieldHash);
  }

  buildForm(): void {
    const fg = this.data.filterGroup;
    this.form = this.fb.group({
      name: [fg?.name || '', Validators.required],
      derivedFieldName: [{ value: fg?.derivedFieldName || '', disabled: !!fg }, [Validators.required, Validators.pattern(/^cmpz~.+/)]],
      derivedFieldValue: [{ value: fg?.derivedFieldValue || '', disabled: !!fg }, Validators.required],
      description: [fg?.description || ''],
      isActive: [fg?.isActive ?? false],
      retroactiveFromDate: [{ value: fg?.retroactiveFromDate || null, disabled: !!fg }],
      conditionOperator: [{ value: fg?.conditionOperator || 'AND', disabled: !!fg }, Validators.required],
      conditions: this.fb.array([])
    });

    if (fg?.conditions?.length) {
      fg.conditions.forEach(c => this.addCondition(c));
    } else {
      this.addCondition();
    }
  }

  loadFilterGroupMap(): void {
    this.filterGroupsService.getFilterGroups().subscribe({
      next: (res: any) => {
        const groups: FilterGroup[] = res.body || [];
        groups.forEach(g => {
          if (g.derivedFieldName) { this.filterGroupMap[g.derivedFieldName] = g; }
        });
      },
      error: () => { }
    });
  }

  loadFields(): void {
    this.generalDataService.getCustomFields('all').subscribe({
      next: (res: any) => {
        const allFields: any[] = res.body || res || [];
        this.availableFields = _.sortBy(allFields.filter((f: any) => !f.key?.startsWith('cmpz~')), 'value');
        this.derivedFields = allFields.filter((f: any) => f.key?.startsWith('cmpz~'));
        this.conditions.controls.forEach(ctrl => {
          const key = ctrl.get('fieldKey').value;
          const hash = ctrl.get('fieldHash').value;
          if (key && hash) { this.loadFieldValues(key, hash); }
        });
      },
      error: () => console.error('Failed to load custom fields')
    });
  }

  loadFieldValues(fieldKey: string, fieldHash: string): void {
    if (this.fieldRawValues[fieldKey]) { return; }
    this.generalDataService.getCustomFieldValues(fieldHash, '').subscribe({
      next: (res: any) => {
        this.fieldRawValues[fieldKey] = res.body || [];
        this.fieldSortPreference[fieldKey] = 'numeric';
        this.applyFieldSort(fieldKey);
      },
      error: () => console.error('Failed to load values for', fieldKey)
    });
  }

  applyFieldSort(fieldKey: string): void {
    const raw = this.fieldRawValues[fieldKey] || [];
    const pref = this.fieldSortPreference[fieldKey] || 'numeric';
    let sorted = _.sortBy(raw, pref === 'numeric' ? 'doc_count' : 'key');
    if (pref === 'numeric') { sorted = _.reverse(sorted); }
    this.fieldValues[fieldKey] = sorted.map(i => i.key);
  }

  toggleFieldSort(fieldKey: string): void {
    if (!fieldKey) { return; }
    this.fieldSortPreference[fieldKey] = this.fieldSortPreference[fieldKey] === 'numeric' ? 'alpha' : 'numeric';
    this.applyFieldSort(fieldKey);
  }

  addCondition(condition?: FilterCondition): void {
    const isEdit = !!this.data?.filterGroup;
    const group = this.fb.group({
      fieldKey: [{ value: condition?.fieldKey || '', disabled: isEdit }],
      fieldLabel: [condition?.fieldLabel || ''],
      fieldHash: [condition?.fieldHash || ''],
      values: [condition?.values || []]
    });
    this.conditions.push(group);
  }

  removeCondition(index: number): void {
    this.conditions.removeAt(index);
  }

  onFieldChange(index: number, fieldKey: string): void {
    const field = this.availableFields.find(f => f.key === fieldKey);
    if (field) {
      this.conditions.controls[index].get('fieldLabel').setValue(field.value);
      this.conditions.controls[index].get('fieldHash').setValue(field.hash);
      this.conditions.controls[index].get('values').setValue([]);
      this.loadFieldValues(fieldKey, field.hash);
    }
  }

  openManualEntry(index: number): void {
    const ctrl = this.conditions.controls[index];
    const fieldLabel = ctrl.get('fieldLabel').value || 'Field';
    const currentValues: string[] = ctrl.get('values').value || [];

    const valuesConfig = ConfigService.getDialogConfig();
    valuesConfig.data = { values: currentValues, fieldLabel };
    const dialogRef = this.dialog.open(FilterGroupValuesDialogComponent, valuesConfig);

    dialogRef.afterClosed().subscribe(result => {
      if (result !== null && result !== undefined) {
        ctrl.get('values').setValue(result);
      }
    });
  }

  getFieldValues(fieldKey: string): string[] {
    return this.fieldValues[fieldKey] || [];
  }

  onSubmit(): void {
    if (this.form.invalid) { return; }
    this.isSaving = true;
    this.errorMessage = '';
    const payload = this.form.getRawValue() as FilterGroup;
    const call$ = this.data.filterGroup
      ? this.filterGroupsService.updateFilterGroup(this.data.filterGroup.id, payload)
      : this.filterGroupsService.createFilterGroup(payload);

    call$.subscribe({
      next: () => {
        this.isSaving = false;
        this.dialogRef.close(true);
      },
      error: (err: any) => {
        this.isSaving = false;
        this.errorMessage = err?.error?.message || 'Failed to save. Please try again.';
      }
    });
  }

  onSaveAndRerun(): void {
    if (this.form.invalid) { return; }
    const confirmRef = this.dialog.open(this.confirmRerunTmpl, ConfigService.getDialogConfigSmall());
    confirmRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) { return; }
      this.isSaving = true;
      this.errorMessage = '';
      const payload = this.form.getRawValue() as FilterGroup;
      this.filterGroupsService.updateFilterGroup(this.data.filterGroup.id, payload).pipe(
        switchMap(() => this.filterGroupsService.rerunFilterGroup(this.data.filterGroup.id))
      ).subscribe({
        next: () => {
          this.isSaving = false;
          this.dialogRef.close(true);
        },
        error: (err: any) => {
          this.isSaving = false;
          this.errorMessage = err?.error?.message || 'Failed to save or rerun. Please try again.';
        }
      });
    });
  }

  viewLogs(): void {
    this.filterGroupsService.getFilterGroup(this.data.filterGroup.id).subscribe({
      next: (res: any) => {
        const fg: FilterGroup = res.body || res;
        const logsConfig = ConfigService.getDialogConfigWide();
        logsConfig.data = { logs: fg.jobLog, name: fg.name, derivedFieldName: fg.derivedFieldName };
        this.dialog.open(FilterGroupLogsDialogComponent, logsConfig);
      },
      error: () => {
        this.errorMessage = 'Failed to load logs.';
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }
}
